import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text } from 'components/ui';
import Carousel from 'components/Carousel';
import Chiclet from 'components/Chiclet';
import RecapCardItem from 'components/RelatedSearches/RecapCardItem.f';
import ChunkletItem from 'components/RelatedSearches/ChunkletItem.f';
import { relatedSearchesVariantSelector } from 'viewModel/selectors/testTarget/relatedSearchesVariantSelector';
import { relatedSearchTermsSelector } from 'selectors/page/relatedSearch/relatedSearchTermsSelector';
import {
    MAX_RELATED_SEARCH_TERMS,
    MIN_RELATED_SEARCH_TERMS,
    RELATED_SEARCH_TYPE_VALUE,
    isRelatedSearchesEligible
} from 'components/RelatedSearches/constants';
import validateMultiTermSearchResponse from 'components/RelatedSearches/schemas/MultiTermSearchResponseSchema';
import useMultiTermSearch from 'hooks/useMultiTermSearch';
import localeUtils from 'utils/LanguageLocale';
import Debounce from 'utils/Debounce';
import Location from 'utils/Location';
import anaUtils from 'analytics/utils';
import anaConsts from 'analytics/constants';
import { RELATED_SEARCHES_EXPERIENCE } from 'constants/TestTarget';
import relatedSearchesEN from 'components/RelatedSearches/locales/relatedSearches_en_US';
import relatedSearchesFR from 'components/RelatedSearches/locales/relatedSearches_fr_CA';

const SEARCH_PATH = '/search';
const DEBOUNCE_MS = 300; // AC-023
const getText = localeUtils.getLocalisedDataFromResource(relatedSearchesEN, relatedSearchesFR);

/**
 * Related Searches section (SBR-3530 shell + SBR-3531 chiclet + SBR-3529 recap card + SBR-3532 chunklet).
 * Self-contained: resolves its own variant + terms via selectors, applies the SAME eligibility
 * predicate the grid reserves on (so a hidden state never leaves an empty reserved cell),
 * caps to 8 (BR-003), and branches on variant to render chiclet rail, recap card grid, or chunklet rail.
 * Returns null in Control / pending / ineligible states (AC-028 render-nothing).
 * Phase 1 (P1-001): Variant branching for CHALLENGER_TWO + CHALLENGER_THREE
 * Phase 4 (P4-004): Manual validation with silent failure (governance-compliant, no Zod)
 * Phase 5 (P5-002): BR-014 cascade hide if term count < 3
 */
// eslint-disable-next-line complexity
function RelatedSearches({ productRowsCount }) {
    const variant = useSelector(relatedSearchesVariantSelector);
    const terms = useSelector(relatedSearchTermsSelector, shallowEqual);
    const [tappedIndex, setTappedIndex] = useState(null);

    // Phase 1 (P1-001): Fetch images for CHALLENGER_TWO variant via multiTermSearch
    // Gate on eligibility to avoid unnecessary requests when ineligible
    const cappedTerms = terms?.slice(0, MAX_RELATED_SEARCH_TERMS) || [];
    const isEligible = isRelatedSearchesEligible(variant, terms, productRowsCount);
    const shouldFetchImages =
        (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO || variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE) && isEligible;

    // SBR-3532: CHALLENGER_THREE uses 1 image per term (chunklet), CHALLENGER_TWO uses 4 (2×2 grid)
    const resultCount = variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE ? 1 : 4;

    const {
        data: multiTermSearchData,
        loading: multiTermSearchLoading,
        error: multiTermSearchError
    } = useMultiTermSearch({
        keywords: shouldFetchImages ? cappedTerms : [],
        resultCount
    });

    // Debounced tap-to-search (AC-023): preventDoubleClick fires once, then suppresses repeats within
    // the window. Navigates the same way as the search bar (SiteSearch) / category chiclets.
    const handleTermClick = useMemo(
        () =>
            Debounce.preventDoubleClick(term => {
                // SBR-3535 AC1: stash the search-type + event for the destination SRP page-view.
                // prevSearchType feeds Adobe Launch's eVar42 ([TMS] Page :: Search Type data element,
                // same key SiteSearch uses); the destination SRP page load fires the single
                // s.t()/TrackState carrying pageName/prop4/prop5/prop22/event233.
                anaUtils.setNextPageData({
                    prevSearchType: RELATED_SEARCH_TYPE_VALUE, // 'related search' → Adobe Launch maps to eVar42
                    events: [anaConsts.Event.INTERNAL_SEARCH] // event1 (event233 added by SRP load)
                });
                Location.navigateTo(null, `${SEARCH_PATH}?keyword=${encodeURIComponent(term)}`);
            }, DEBOUNCE_MS),
        []
    );

    // SAME predicate as the grid reservation gate: Control / pending / < 3 terms / < 2 rows => render nothing.
    if (!isEligible) {
        return null;
    }

    const title = getText('relatedSearchesTitle');

    // Phase 4 (P4-004): Manual validation for CHALLENGER_TWO variant (governance-compliant, no Zod)
    let validatedData = null;

    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO && multiTermSearchData) {
        const validationResult = validateMultiTermSearchResponse(multiTermSearchData);

        if (!validationResult.success) {
            if (typeof Sephora !== 'undefined') {
                Sephora?.logger?.warn('[RelatedSearches] multiTermSearch validation failed:', validationResult.error);
            }

            return null; // P4-004: section hides on validation error
        }

        validatedData = { results: validationResult.data };
    }

    // Phase 5 (P5-002): BR-014 cascade - hide section if < 3 TERMS (not < 3 cards with images)
    // BR-014: "Cards are NEVER hidden for missing images" - RecapCardItem handles 0 images with placeholders
    let filteredResults = [];

    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO && validatedData) {
        filteredResults = validatedData.results; // Show ALL cards (AC-013, AC-014)

        // P5-002: Cascade hide if total TERMS < 3 (not filtered by image count)
        if (validatedData.results.length < MIN_RELATED_SEARCH_TERMS) {
            // SBR-3535 ANALYTICS HOOK: fire load_failed event here (out of scope this bundle)
            return null; // Section hidden when term count < 3
        }
    }

    // SBR-3532: CHALLENGER_THREE image map (BR-014 compliant — never hide for missing images)
    // On error or validation failure, chunkletImageMap stays null => all chunklets render with placeholders
    let chunkletImageMap = null;

    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE && multiTermSearchData && !multiTermSearchError) {
        const validationResult = validateMultiTermSearchResponse(multiTermSearchData);

        if (validationResult.success) {
            // Build a term -> images lookup for chunklet rendering
            chunkletImageMap = {};
            validationResult.data.forEach(result => {
                chunkletImageMap[result.searchTerm] = result.thumbnailImages || [];
            });
        } else if (typeof Sephora !== 'undefined') {
            Sephora?.logger?.warn('[RelatedSearches] multiTermSearch validation failed:', validationResult.error);
        }
    }

    // CHALLENGER_TWO: Error or no data (once fetch has settled) => silent failure (already handled by validation above)
    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO && !multiTermSearchLoading && (multiTermSearchError || !filteredResults.length)) {
        return null;
    }

    // ---- VARIANT BRANCHING (Phase 1 P1-001): resolve items + carousel config, single Carousel render ----
    let items;
    let carouselProps = {
        itemWidth: 'auto',
        gap: 2,
        // Outdent through the outer Box's paddingLeft/paddingRight={2}, then scrollPadding's
        // spacer <li>s re-add that same inset inside the scroll track — net position unchanged,
        // but now the item shadows have real dead-space to bleed into at the scroll extremes.
        marginX: -2,
        scrollPadding: 2,
        showArrowOnHover: false
    };

    if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_TWO) {
        // Recap card grid with 2×2 images. While the fetch is in flight, terms are already known
        // (from the selector, not the fetch) so render real cards immediately — only the image
        // slots show a per-image skeleton (P1-002/AC-022) instead of blocking the whole section.
        const recapResults = multiTermSearchLoading ? cappedTerms.map(term => ({ searchTerm: term, thumbnailImages: [] })) : filteredResults;

        items = recapResults.map((result, i) => (
            <RecapCardItem
                key={result.searchTerm}
                term={result.searchTerm}
                images={result.thumbnailImages}
                isLoading={multiTermSearchLoading}
                onClick={() => {
                    setTappedIndex(i);
                    handleTermClick(result.searchTerm);
                }}
                isActive={tappedIndex === i}
            />
        ));
        carouselProps = { ...carouselProps, paddingY: 2, arrowCircleWidth: 48 };
    } else if (variant === RELATED_SEARCHES_EXPERIENCE.CHALLENGER_THREE) {
        // SBR-3532: Chunklet variant (term label + single product image)
        // BR-014: Chunklets are NEVER hidden for missing images — render with placeholders on error/validation failure
        items = cappedTerms.map((term, i) => {
            const images = chunkletImageMap?.[term] || [];

            return (
                <ChunkletItem
                    key={term}
                    term={term}
                    images={images}
                    isLoading={multiTermSearchLoading}
                    onClick={() => {
                        setTappedIndex(i);
                        handleTermClick(term);
                    }}
                />
            );
        });
        carouselProps = { ...carouselProps, gap: 3, paddingY: 2, arrowCircleWidth: 32 };
    } else {
        // CHALLENGER_ONE: Chiclet rail
        items = cappedTerms.map((term, i) => (
            <Chiclet
                key={term}
                is='button'
                type='button'
                variant='shadow'
                isActive={tappedIndex === i}
                aria-pressed={tappedIndex === i}
                aria-label={getText('searchForTerm', [term])}
                data-cnstrc-related-search-term={term}
                onClick={() => {
                    setTappedIndex(i);
                    handleTermClick(term);
                }}
                children={term}
            />
        ));
        // variant='shadow' Chiclets need the same vertical breathing room as challenger
        // 2/3 items, otherwise the carousel's overflow clips the top/bottom box-shadow.
        carouselProps = { ...carouselProps, paddingY: 2, arrowCircleWidth: 32 };
    }

    return (
        <Box
            paddingTop={[1, 2]}
            paddingBottom={[1, 2]}
            paddingLeft={2}
            paddingRight={2}
        >
            <Text
                is='h2'
                fontSize={['base', 'md']}
                fontWeight='bold'
                lineHeight='tight'
                marginBottom={2}
                children={title}
            />
            <Box
                css={styles.noArrowMargin}
                data-cnstrc-related-searches
            >
                <Carousel
                    items={items}
                    arrowVariant='circle'
                    ariaLabel={title}
                    {...carouselProps}
                />
            </Box>
        </Box>
    );
}

// Co-located styles per UFE convention
const styles = {
    // CarouselArrow gives arrows a marginX derived from scrollPadding; remove it here so
    // the arrows sit flush against the rail edge instead of inset from it.
    noArrowMargin: {
        '.Carousel-control': {
            marginLeft: 0,
            marginRight: 0
        }
    }
};

RelatedSearches.propTypes = {
    productRowsCount: PropTypes.number
};

export default wrapFunctionalComponent(RelatedSearches, 'RelatedSearches');
